import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import * as taskActions from "@rentiohq/shared-frontend/dist/reduxV2/task/task.actions";
import * as taskSelectors from "@rentiohq/shared-frontend/dist/reduxV2/task/task.selectors";
import { IComment } from "@rentiohq/shared-frontend/dist/types/comment.types";
import { ITask } from "@rentiohq/shared-frontend/dist/types/task.types";
import { formatDateAndTime } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { EAssets } from "@rentiohq/web-shared-next/dist/assets";
import { EFontWeights } from "@rentiohq/web-shared-next/dist/themes/types";
import {
  Box,
  Button,
  Icon,
  Link,
  Stack,
  Typography,
  TextField,
  SpinningLoader,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { EButtonVariants } from "@rentiohq/web-shared-next/dist/utils/types/button";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootStore } from "redux/reducers";
import { CommentStyles } from "./Comments.styles";

interface IProps {
  task: ITask;
}

const INLINE_FETCH_LIMIT = 7;

export const Comments = ({ task }: IProps) => {
  const dispatch = useDispatch();

  const { user } = authHooks.useSelf();

  const sx = CommentStyles();

  const [hasFocus, setHasFocus] = React.useState<boolean>(false);
  const [query, setQuery] = React.useState<string>("");

  const isFetchingComments = useSelector((state: IRootStore) =>
    taskSelectors.isFetchingComments(state, task.id),
  );

  const isCreatingComment = useSelector((state: IRootStore) =>
    taskSelectors.isCreatingComment(state, task.id),
  );

  const comments = useSelector(
    (state: IRootStore) => taskSelectors.getTaskComments(state, task.id) || [],
  );

  const isCommentsExhausted = useSelector((state: IRootStore) =>
    taskSelectors.isCommentsExhausted(state, task.id),
  );

  const fetchData = (refetch: boolean) => {
    dispatch(
      taskActions.getTaskCommentsStart.getAction({
        taskId: task.id,
        refetch,
        limit: INLINE_FETCH_LIMIT,
      }),
    );
  };

  React.useEffect(() => {
    fetchData(true);
  }, []);

  const handleClickDelete = (comment: IComment) => {
    dispatch(
      taskActions.deleteTaskCommentStart.getAction({
        taskId: task.id,
        commentId: comment.id,
      }),
    );
  };

  const handlePressShowMore = () => {
    fetchData(false);
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setQuery(event.target.value);
  };

  const handleSubmit = () => {
    if (query.length > 0) {
      dispatch(
        taskActions.addTaskCommentStart.getAction({
          taskId: task.id,
          content: query,
        }),
      );
      setQuery("");
    }
  };

  const renderShowAllButton = () => {
    // Every loaded comment is rendered, so the button only has something to
    // add while older pages remain on the server.
    if (isCommentsExhausted || comments.length === 0) {
      return;
    }

    return (
      <Link
        label={getLocalizedText("task.inline.cta.show_more")}
        underlined={false}
        onClick={handlePressShowMore}
      />
    );
  };

  const renderAddCommentField = () => {
    let value = query;
    if (!hasFocus) {
      const splitted = query.split("\n")[0];
      value = /\r|\n/.exec(query) ? `${splitted}...` : splitted;
    }

    return (
      <Stack direction="row" gap={1} alignItems="center" width="100%" my={1}>
        <TextField
          value={value}
          fullWidth
          multiline={hasFocus}
          autoFocus={hasFocus}
          onChange={handleChange}
          onFocus={() => setHasFocus(true)}
          onBlur={() => setHasFocus(false)}
          placeholder={getLocalizedText(
            "tenant.task.public_comments.placeholder",
          )}
        />
        <Button
          variant={EButtonVariants.Outlined}
          onClick={handleSubmit}
          size="large"
          disabled={isCreatingComment}
          sx={sx.content.submitBtn}
        >
          {getLocalizedText("system.submit")}
        </Button>
      </Stack>
    );
  };

  const renderCommentItem = (comment: IComment) => {
    const { createdAt, content, createdByAccount } = comment;
    const isMe = createdByAccount.id === user?.personalContactId;

    return (
      <Box sx={sx.content.comment}>
        {isMe && (
          <Box
            onClick={() => handleClickDelete(comment)}
            sx={sx.content.closeIcon}
          >
            <Icon path={EAssets.CloseIcon} width="14px" height="14px" />
          </Box>
        )}
        <Typography variant="caption">{`${getName(
          createdByAccount,
        )} ${formatDateAndTime(createdAt)}`}</Typography>
        <Typography variant="subtitle2" fontWeight={EFontWeights.Regular}>
          {content}
        </Typography>
      </Box>
    );
  };

  return (
    <Stack gap={1}>
      <Stack direction="row" gap={1}>
        <Typography variant="h5">
          {getLocalizedText("tenant.task.public_comments.title")}
        </Typography>
        {(isFetchingComments || isCreatingComment) && <SpinningLoader />}
      </Stack>
      {renderAddCommentField()}
      {comments?.length === 0 && (
        <Typography textAlign="center">
          {getLocalizedText("comment.empty.info")}
        </Typography>
      )}
      <Stack gap={2}>
        {comments.map(comment => renderCommentItem(comment))}
      </Stack>
      {renderShowAllButton()}
    </Stack>
  );
};
